import { useState } from "react";
import { Button } from "@/components/ui/button";
import { BarChart3, ZoomIn, X, ImageOff } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import { useSfx } from "@/hooks/useSfx";
import LumiHolocard from "@/components/lessons/LumiHolocard";
import { getLumiHolocardMomentsFor } from "@/lib/audio/lumiHolocardVoiceMap";

interface InfographicStageProps {
  infographicUrl?: string | null;
  infographicTitle?: string;
  infographicAlt?: string;
  continueLabel?: string;
  onContinue: () => void;
  /** Enables optional Lumi holocard support on approved beta lessons. */
  lessonId?: string | null;
}

const InfographicStage = ({
  infographicUrl,
  infographicTitle,
  infographicAlt = "Lesson infographic",
  continueLabel = "Continue →",
  onContinue,
  lessonId,
}: InfographicStageProps) => {
  const [loaded, setLoaded] = useState(false);
  const [errored, setErrored] = useState(false);
  const [enlarged, setEnlarged] = useState(false);
  const { playSfx } = useSfx();

  const hasImage = !!infographicUrl && !errored;
  const holocard = getLumiHolocardMomentsFor(lessonId, "infographic")[0];

  return (
    <div className="min-h-screen bg-background flex flex-col items-center justify-center gap-6 px-4 py-8">
      {/* Section Label */}
      <div className="flex items-center gap-2">
        <BarChart3 className="w-4 h-4 text-primary/70" />
        <span className="text-xs font-heading uppercase tracking-widest text-muted-foreground">
          Visual Breakdown
        </span>
      </div>

      {infographicTitle && (
        <h2 className="font-heading font-bold text-xl md:text-2xl text-foreground text-center max-w-2xl leading-tight">
          {infographicTitle}
        </h2>
      )}

      {hasImage ? (
        <div className="w-full max-w-4xl relative">
          {/* Loading skeleton */}
          {!loaded && (
            <Skeleton className="w-full aspect-[16/10] rounded-2xl" />
          )}

          {/* Image container */}
          <button
            onClick={() => setEnlarged(true)}
            className={`group w-full rounded-2xl overflow-hidden border border-primary/20 shadow-[0_0_30px_hsl(var(--primary)/0.1)] hover:shadow-[0_0_40px_hsl(var(--primary)/0.2)] transition-shadow duration-300 cursor-zoom-in ${
              !loaded ? "hidden" : "block"
            }`}
          >
            <img
              src={infographicUrl!}
              alt={infographicAlt}
              className="w-full h-auto object-contain bg-muted/30"
              onLoad={() => setLoaded(true)}
              onError={() => setErrored(true)}
            />
            <div className="absolute bottom-3 right-3 opacity-0 group-hover:opacity-100 transition-opacity duration-200 bg-card/80 backdrop-blur-sm rounded-lg px-2.5 py-1.5 flex items-center gap-1.5 border border-border/50">
              <ZoomIn className="w-3.5 h-3.5 text-muted-foreground" />
              <span className="text-xs text-muted-foreground font-body">Click to enlarge</span>
            </div>
          </button>
        </div>
      ) : (
        /* Fallback: no infographic */
        <div className="w-full max-w-md rounded-2xl border border-border bg-card/50 p-10 flex flex-col items-center gap-4 text-center">
          <div className="w-14 h-14 rounded-xl bg-muted/50 flex items-center justify-center">
            <ImageOff className="w-6 h-6 text-muted-foreground/60" />
          </div>
          <div className="space-y-1">
            <p className="font-heading font-semibold text-sm text-foreground/80">
              Infographic coming soon
            </p>
            <p className="text-xs text-muted-foreground font-body">
              This visual is being prepared. You can continue to the quiz.
            </p>
          </div>
        </div>
      )}

      <Button
        onClick={() => { playSfx("soft_swoosh"); onContinue(); }}
        className="mt-2 px-8 py-3 bg-accent/15 border border-accent/30 text-accent font-heading font-semibold hover:bg-accent/25 shadow-[0_0_20px_hsl(var(--accent)/0.2)]"
      >
        {continueLabel}
      </Button>

      {holocard && (
        <LumiHolocard momentId={holocard.momentId} title={infographicTitle} />
      )}

      {/* Lightbox overlay */}
      {enlarged && hasImage && (
        <div
          className="fixed inset-0 z-[100] bg-background/95 backdrop-blur-md flex items-center justify-center p-4"
          onClick={() => setEnlarged(false)}
        >
          <button
            onClick={() => setEnlarged(false)}
            className="absolute top-4 right-4 z-10 w-10 h-10 rounded-full bg-card/80 border border-border/50 flex items-center justify-center hover:bg-card transition-colors"
          >
            <X className="w-5 h-5 text-foreground" />
          </button>
          <img
            src={infographicUrl!}
            alt={infographicAlt}
            className="max-w-full max-h-full object-contain rounded-xl"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </div>
  );
};

export default InfographicStage;
